import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const CustomerExperienceAndCPQ = () => {
  const phrases = [
    "Revenue Optimization",
    "Omnichannel Commerce",
    "Frictionless Quoting"
  ];

  const cardData = [
    { type: 'purple-clay', content: '⚙️' },
    { type: 'smooth-wave', content: '💰' },
    { type: 'abstract-blocks', content: '📊' },
    { type: 'pink-sphere', content: '🤝' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "⚙️", title: "Intelligent Product Configuration", desc: "Design nested product dependencies, bundle constraints, and systemic hardware/software validation paths to eliminate bad orders entirely." },
    { icon: "💰", title: "Dynamic Pricing & Margin Optimization", desc: "Deploy matrix pricing pipelines, volume discounting bands, regional currencies, and granular margin safeguard floors for sales reps." },
    { icon: "📄", title: "Automated Document & Quote Generation", desc: "Instantly build beautiful, pixel-perfect PDF proposals, localized terms sheets, and executive cost summaries with single-click actions." },
    { icon: "🤝", title: "B2B Self-Service Commerce Portals", desc: "Empower partners and enterprise buyers to configure intricate solutions, request volume splits, and reorder assets autonomously." },
    { icon: "🔄", title: "Subscription & Renewal Lifecycle Systems", desc: "Manage multi-year contracts, mid-term co-terming adjustments, usage-based invoicing ramps, and automated expansion plays." },
    { icon: "🔌", title: "ERP & CRM Ecosystem Orchestration", desc: "Synchronize approved transactional data structures seamlessly between Salesforce/HubSpot frontends and SAP/Oracle back-office databases." },
    { icon: "🛡️", title: "Multi-Level Margin Approval Workflows", desc: "Establish strict rule-based routing loops that loop in financial stakeholders automatically if deep pricing exceptions are triggered." }
  ];

  const advancedServices = [
    { icon: "📊", title: "Real-Time Win/Loss Telemetry", desc: "Track precisely where customers dwell inside proposal dashboards, uncovering hidden drop-offs and pricing friction points instantly." },
    { icon: "🔮", title: "AI-Driven Cross-Sell Optimization", desc: "Infuse smart recommendation blocks that suggest contextual product expansions right inside the live quotation window." },
    { icon: "⚖️", title: "Dynamic Legal & Compliance Guardrails", desc: "Inject specific regional regulatory clauses natively based on product category, geographic footprint, or entity configuration." },
    { icon: "🌐", title: "Omnichannel Quote Continuity", desc: "Allow enterprise teams to seamlessly initiate complex deals inside a mobile terminal and complete them inside a desktop browser." },
    { icon: "⚡", title: "High-Velocity Batch Re-Pricing Engines", desc: "Recalculate millions of active global contract line-items within seconds during sudden component cost shifts or updates." },
    { icon: "✍️", title: "Integrated E-Signature Pathways", desc: "Embed legal confirmation hooks directly inside responsive customer web agreements to compress deal close cycles." },
    { icon: "🧮", title: "Advanced Revenue Recognition Sync", desc: "Map complex split-payment distributions and deferred billing configurations directly into global standard accounting nodes." },
    { icon: "📦", title: "Supply Chain & Allocation Checks", desc: "Connect active checkout procedures directly with regional warehouse inventory matrices to display definitive fulfillment dates." },
    { icon: "🧪", title: "Visual Catalog Regression Testing", desc: "Run background behavioral passes against pricing logic rules to ensure zero system calculation glitches during catalog updates." }
  ];

  const technologies = {
    "Core CPQ Engine Focus": ["Salesforce CPQ & Revenue Cloud", "Oracle CPQ (BigMachines)", "Conga / Apttus Commerce Hub", "Custom Microservices Engines"],
    "Customer Touchpoint Layers": ["React.js / Next.js Storefronts", "Vue.js / Tailwind UI Frameworks", "Mobile Swift & Kotlin Components", "Headless Commerce GraphQL APIs"],
    "Ecosystem Integration Links": ["MuleSoft / Dell Boomi Hubs", "Apache Kafka Event Pipelines", "RESTful Legacy API Adapters", "Webhooks Notification Grids"],
    "Data Infrastructure & ERP": ["SAP S/4HANA & NetWeaver", "Oracle ERP Cloud / NetSuite", "Microsoft Dynamics 365 Core", "PostgreSQL / Redis Cache Databases"],
    "Analytics & Optimization": ["Mixpanel User Journey Logging", "Tableau Enterprise Dashboards", "Snowflake Revenue Warehouses", "Segment Customer Data Hubs"]
  };

  const processSteps = [
    { num: "01", title: "Discovery & Catalog Auditing", desc: "Deconstructing messy product structures, commercial pricing strategies, and historical operational blockers." },
    { num: "02", title: "Constraint Architecture Design", desc: "Mapping out complex bundling regulations, exclusion dependencies, and margin tier requirements clearly." },
    { num: "03", title: "Sprint-Based System Configuration", desc: "Programming strict product rules, formatting calculation scripts, and assembling clean user interface pages." },
    { num: "04", title: "Enterprise System Connection", desc: "Linking data lines securely between live front-end CRM tools, pricing models, and core back-office ERP registers." },
    { num: "05", title: "Rigorous Transactional Validation", desc: "Running deep edge-case evaluations, pricing matrix load loops, and cross-channel checkout runs." },
    { num: "06", title: "Production Deployment", desc: "Migrating approved catalog builds to active business spaces smoothly with zero impact on current operations." },
    { num: "07", title: "Behavioral Analytics Tracking", desc: "Evaluating system performance metrics, reviewing close cycles, and constantly tuning pricing setups." }
  ];

  const industries = [
    "High-Tech & Enterprise SaaS Providers", "Telecom & Connectivity Networks", "Medical Equipment & Device Builders",
    "Heavy Industrial Manufacturing Teams", "Global Logistics & Intermodal Shippers", "Energy Suppliers & Utilities Providers",
    "Professional Services & Advisory Units", "Wholesale Distributors & Modern B2B Marketplaces", "Aerospace & Defence Contractors",
    "Insurance Providers & Underwriters", "Automotive Fleet Supply Networks", "Complex Construction & EPC Firms"
  ];

  const designBenefits = [
    "99% Reduction in Order Errors", "Accelerated Speed-to-Quote Times", "Protected Gross Margin Integrity",
    "Frictionless Self-Service UX Scales", "Flawless Multi-Currency Handling", "Rapid New Product Rollout Speeds",
    "Auditable Contract Compliance Paths", "Unified Omnichannel Buyer Views"
  ];

  const whyChooseUs = [
    "Certified CPQ & CX Architects", "Proven Commercial Scale Matrices", "Modern User Experience Focus",
    "Deep ERP System Integration Depth", "Security-First Enterprise Architecture", "High-Velocity Pricing Pipelines",
    "Comprehensive Post-Launch Maintenance Support", "Transparent Data Migration Models", "Enterprise-Grade SLA Commitments",
    "Experienced Cloud Ecosystem Designers", "Agile Co-Development Methodology", "100% Core Transactional Stability Commitments"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Customer Experience & CPQ Services | CRM, CX & Configure Price Quote Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Customer Experience & CPQ Services | CRM, CX & Configure Price Quote Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. delivers Customer Experience (CX) and CPQ services, including CRM solutions, Configure Price Quote (CPQ) implementation, customer journey optimization, sales automation, quote management, digital customer engagement, and enterprise CX transformation."
        />

        <meta
          name="keywords"
          content="Customer Experience, Customer Experience Services, CPQ Services, Configure Price Quote, CPQ Implementation, CRM Solutions, Customer Journey Management, Sales Automation, Quote Management, Digital Customer Experience, Customer Engagement, Salesforce CPQ, Oracle CPQ, SAP CPQ, CRM Consulting, Customer Experience Management, Enterprise CX Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/customer-experience-and-cpq"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Customer Experience & CPQ Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Enhance customer satisfaction and streamline sales with Customer Experience and CPQ solutions from Affsoft IT Solutions Pvt. Ltd. We provide CRM consulting, CPQ implementation, customer engagement, and digital transformation services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/customer-experience-and-cpq"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Customer Experience & CPQ Services | CRM & Configure Price Quote Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Deliver exceptional customer experiences with CX and CPQ services from Affsoft IT Solutions Pvt. Ltd. We specialize in CRM, CPQ implementation, sales automation, customer engagement, and enterprise digital transformation."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="cx-cpq-engineering-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0b132b' // Dark navy commerce context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Customer Experience &nbsp; and &nbsp;</span>
              <span>CPQ Transformation </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-success fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Customer Experience & CPQ</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the friction between sales complexities and digital customer journeys. Our advanced Customer Experience and CPQ engineering solutions unite modern storefronts with intelligent configuration engines, ensuring accurate pricing, rapid quote turnarounds, and highly reliable buying experiences at scale.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Connect with Our Experts</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-success fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success text-white px-3 py-2 rounded-pill mb-2 text-uppercase">Revenue Architecture</span>
              <h2 className="fw-bold text-dark display-6">Transform Complex Buying Structures Into Clean Growth Vehicles</h2>
              <p className="text-muted mt-3">
                Manual spreadsheets and disconnected internal quoting setups open the door to pricing leaks, margin erosion, and lost revenue. At Affsoft IT Solutions Pvt. Ltd., we engineer scalable transactional frameworks that automate core buying processes seamlessly.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Strategic Operational Advantages of Advanced CPQ Deployments</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED MODERNIZATION OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success text-white px-3 py-2 rounded-pill mb-2 text-uppercase">Intelligent Quoting</span>
              <h2 className="fw-bold text-dark display-6">High-Fidelity Interaction Layers for Modern Commerce</h2>
              <p className="text-muted mt-3">
                We extend traditional CRM capabilities into deeply optimized transactional systems. From rendering clean product builders to processing high-speed pricing matrices, our deployments support multi-channel expansion securely.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success text-white px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Technology Stack</span>
              <h2 className="fw-bold display-6">Platforms & Integration Frameworks We Master</h2>
              <p className="text-light-50">We structure, connect, and monitor enterprise commerce ecosystems using leading operational platforms.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-success mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-success opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Implementation Journey</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered CPQ Delivery Roadmap</h2>
              <p className="text-muted">A structured, data-tested framework designed to move your catalog systems safely from analysis to deployment.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-success border-4 h-100 shadow-sm">
                    <div className="fw-bold text-success display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Optimize</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine rigorous configuration validation with expert system architecture to optimize your sales pipelines.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-success fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default CustomerExperienceAndCPQ;